import { DIFF_DELETE, DIFF_INSERT } from 'diff-match-patch';

import { DELETE_PLACEHOLDER, RANGE_END, RANGE_START } from './constants';
import {
  diffsToDiffSegments,
  isRangeEnd,
  isRangeStart,
  splitTextByRangeMarkers,
} from './diff-utils';
import type { DiffSegment } from './types';
import { diffWordsRaw, diffWordsSemantic } from './word-diff';

/**
 * Extended DiffSegment type that tracks whether the segment came from a placeholder
 * (i.e., restored deleted text that exists in the document but shouldn't be
 * counted as new content).
 */
type TrackedDiffSegment = DiffSegment & { _fromPlaceholder?: boolean };

/**
 * Checks if a change is a delete operation with the given id.
 */
function isDeleteWithId(change: TrackedDiffSegment | undefined, id: string): boolean {
  return change !== undefined && change.op === 'delete' && change.id === id;
}

/**
 * Layers new changes on top of an existing diff.
 *
 * Takes an existing paragraph expressed as a diff and computes the changes
 * needed to transform it to the target text. Already-deleted text cannot be
 * deleted again (it's replaced with placeholders during diff computation).
 *
 * The returned diff contains only the NEW changes to add on top of the existing
 * diff - it does not include the existing diff's operations.
 */
export function layerDiffs(
  existingDiff: DiffSegment[],
  targetText: string,
  id: string
): DiffSegment[] {
  /**
   * Step 1: Build string with placeholders for deleted text.
   * - EQUAL text stays as-is
   * - INSERT text stays as-is (insertions can be deleted later)
   * - DELETE text is replaced with placeholder (one placeholder per deletion)
   */
  let stringWithPlaceholders = '';
  const deletedTexts: string[] = [];
  for (const change of existingDiff) {
    if (change.op === 'delete') {
      stringWithPlaceholders += DELETE_PLACEHOLDER;
      deletedTexts.push(change.text);
    } else {
      stringWithPlaceholders += change.text;
    }
  }

  // Step 2: Compute word-level diff against target (without semantic cleanup to preserve placeholders)
  const rawDiffs = diffWordsRaw(stringWithPlaceholders, targetText);

  /**
   * Step 3: Process placeholders in the diff result.
   * - Placeholders in DELETE operations → restore as EQUAL (text exists in document, just marked deleted)
   * - Placeholders in EQUAL operations → restore as EQUAL
   * - Other text is processed normally
   * - Mark placeholder-derived segments with _fromPlaceholder flag
   */
  const result: TrackedDiffSegment[] = [];
  let deletedIndex = 0;

  for (const [op, text] of rawDiffs) {
    // Process the text, handling placeholders
    let remaining = text;
    while (remaining.length > 0) {
      const placeholderIndex = remaining.indexOf(DELETE_PLACEHOLDER);

      if (placeholderIndex === -1) {
        // No placeholder in remaining text
        if (op === DIFF_INSERT) {
          result.push({ id, op: 'insert', text: remaining });
        } else if (op === DIFF_DELETE) {
          result.push({ id, op: 'delete', text: remaining });
        } else {
          result.push({ op: 'equal', text: remaining });
        }
        break;
      }

      // Handle text before the placeholder
      if (placeholderIndex > 0) {
        const beforePlaceholder = remaining.slice(0, placeholderIndex);
        if (op === DIFF_INSERT) {
          result.push({ id, op: 'insert', text: beforePlaceholder });
        } else if (op === DIFF_DELETE) {
          result.push({ id, op: 'delete', text: beforePlaceholder });
        } else {
          result.push({ op: 'equal', text: beforePlaceholder });
        }
      }

      /**
       * Restore the deleted text as EQUAL (it exists in the document).
       * Mark as _fromPlaceholder so it won't be counted in newText during semantic cleanup.
       */
      const deletedText = deletedTexts[deletedIndex++];
      if (deletedText) {
        result.push({ _fromPlaceholder: true, op: 'equal', text: deletedText });
      }

      // Continue with text after the placeholder
      remaining = remaining.slice(placeholderIndex + 1);
    }
  }

  /**
   * Step 4: Split inserts containing range markers into separate inserts.
   * This is needed so the expansion logic can identify isolated RANGE_START markers.
   */
  const splitResult: TrackedDiffSegment[] = [];

  for (const change of result) {
    if (
      change.op === 'insert' &&
      (change.text.includes(RANGE_START) || change.text.includes(RANGE_END))
    ) {
      // Split this insert by range markers
      const parts = splitTextByRangeMarkers(change.text);
      for (const part of parts) {
        if (part.length > 0) {
          splitResult.push({ id: change.id, op: 'insert', text: part });
        }
      }
    } else {
      splitResult.push(change);
    }
  }

  /**
   * Step 5: Expand empty ranges to include placeholder-derived equals to the left.
   * Only expand when RANGE_START is immediately followed by RANGE_END (empty range).
   * This handles the "accept delete" case where the user places empty range markers
   * at the position of a delete. We don't expand when the range has content, as that
   * would override the user's explicit range placement.
   */
  for (let i = splitResult.length - 1; i > 0; i--) {
    const change = splitResult[i];
    const nextDiffSegment = splitResult[i + 1];
    const prev = splitResult[i - 1];

    if (!change || !prev) continue;

    if (isRangeStart(change)) {
      // Only expand if this is an empty range (RANGE_START immediately followed by RANGE_END)
      if (!nextDiffSegment || !isRangeEnd(nextDiffSegment)) {
        continue;
      }

      // Check if previous element is a placeholder-derived equal
      if (prev._fromPlaceholder) {
        // Don't swap if the element before that is RANGE_END (avoid disrupting other ranges)
        if (i > 1) {
          const beforePrev = splitResult[i - 2];
          if (beforePrev && isRangeEnd(beforePrev)) {
            continue;
          }
        }
        // Swap: move RANGE_START before the placeholder-derived equal
        splitResult[i - 1] = change;
        splitResult[i] = prev;
      }
    }
  }

  /**
   * Step 6: Convert placeholder-derived EQUALs to DELETEs when sandwiched between DELETEs.
   * This ensures word-level granularity: if the existing diff had a character-level deletion
   * within a word, and the new target restores that character, we should delete the entire
   * word and insert the clean word.
   *
   * IMPORTANT: Only convert when BETWEEN two DELETEs (both sides), not just adjacent to one.
   * This distinguishes character-level deletions within a word (e.g., "bro" + deleted "w" + "n")
   * from word-level deletions that happen to be adjacent (e.g., deleted "beta " next to "gamma").
   */
  for (let i = 0; i < splitResult.length; i++) {
    const change = splitResult[i];
    if (change._fromPlaceholder && change.op === 'equal') {
      const prev = splitResult[i - 1];
      const next = splitResult[i + 1];
      // Only convert if sandwiched BETWEEN two DELETEs with the same id
      if (isDeleteWithId(prev, id) && isDeleteWithId(next, id)) {
        splitResult[i] = { id, op: 'delete', text: change.text };
      }
    }
  }

  // Step 7: Merge adjacent segments of the same type (preserving _fromPlaceholder flag)
  const merged = mergeTrackedDiffSegments(splitResult);

  // Step 8: Apply semantic cleanup on the cleaned result
  return applySemanticCleanup(merged, id);
}

/**
 * Merges adjacent TrackedDiffSegment segments of the same type, preserving the
 * _fromPlaceholder flag. If any merged segment has _fromPlaceholder, the
 * result will have it too.
 */
function mergeTrackedDiffSegments(changes: TrackedDiffSegment[]): TrackedDiffSegment[] {
  if (changes.length === 0) return changes;

  const result: TrackedDiffSegment[] = [];
  for (const change of changes) {
    const last = result[result.length - 1];
    // Merge adjacent segments of the same operation type (and same id for insert/delete).
    if (last && last.op === change.op) {
      if (change.op === 'equal') {
        last.text += change.text;
        // Preserve _fromPlaceholder if either segment has it
        if (change._fromPlaceholder) {
          last._fromPlaceholder = true;
        }
        continue;
      }
      if (
        last.op !== 'equal' &&
        (change.op === 'insert' || change.op === 'delete') &&
        last.id === change.id
      ) {
        last.text += change.text;
        continue;
      }
    }
    // Create new segment (copy all properties including _fromPlaceholder)
    if (change.op === 'equal') {
      const newDiffSegment: TrackedDiffSegment = { op: 'equal', text: change.text };
      if (change._fromPlaceholder) {
        newDiffSegment._fromPlaceholder = true;
      }
      result.push(newDiffSegment);
    } else if (change.op === 'insert') {
      result.push({ id: change.id, op: 'insert', text: change.text });
    } else {
      result.push({ id: change.id, op: 'delete', text: change.text });
    }
  }
  return result;
}

/**
 * Strips RANGE_START and RANGE_END markers from text, returning the clean text
 * and an array of marker positions (content offsets where markers should be
 * re-inserted after diffing).
 */
function stripRangeMarkers(text: string) {
  const markers: { char: string; contentOffset: number }[] = [];
  let cleanText = '';
  for (const ch of text) {
    if (ch === RANGE_START || ch === RANGE_END) {
      markers.push({ char: ch, contentOffset: cleanText.length });
    } else {
      cleanText += ch;
    }
  }
  return { cleanText, markers };
}

/**
 * Re-inserts range markers into a diff result at the content offsets where they
 * originally appeared in the new text.
 *
 * Walks through the diff segments tracking the "new text offset" (which advances
 * for equal and insert segments but not delete). When a marker's content offset
 * is reached, it's spliced in as an insert segment, splitting surrounding equal
 * or insert segments if necessary.
 */
function spliceMarkersIntoDiffs(
  diffs: [number, string][],
  markers: { char: string; contentOffset: number }[],
  id: string
): DiffSegment[] {
  if (markers.length === 0) {
    return diffsToDiffSegments(diffs, id);
  }

  const result: DiffSegment[] = [];
  let markerIdx = 0;
  let newOffset = 0;

  for (const [op, text] of diffs) {
    if (op === DIFF_DELETE) {
      /**
       * Delete doesn't advance new offset.
       * Insert any markers whose offset matches the current position BEFORE the delete.
       */
      while (markerIdx < markers.length && markers[markerIdx]!.contentOffset <= newOffset) {
        result.push({ id, op: 'insert', text: markers[markerIdx]!.char });
        markerIdx++;
      }
      result.push({ id, op: 'delete', text });
    } else {
      // Equal or Insert — advance new offset, splicing markers within the segment
      let segStart = 0;
      while (
        markerIdx < markers.length &&
        markers[markerIdx]!.contentOffset < newOffset + text.length
      ) {
        const splitPoint = markers[markerIdx]!.contentOffset - newOffset;
        if (splitPoint > segStart) {
          const beforeText = text.slice(segStart, splitPoint);
          if (op === DIFF_INSERT) {
            result.push({ id, op: 'insert', text: beforeText });
          } else {
            result.push({ op: 'equal', text: beforeText });
          }
        }
        result.push({ id, op: 'insert', text: markers[markerIdx]!.char });
        segStart = splitPoint;
        markerIdx++;
      }
      if (segStart < text.length) {
        const remainingText = text.slice(segStart);
        if (op === DIFF_INSERT) {
          result.push({ id, op: 'insert', text: remainingText });
        } else {
          result.push({ op: 'equal', text: remainingText });
        }
      }
      newOffset += text.length;
    }
  }

  // Any remaining markers at the end
  while (markerIdx < markers.length) {
    result.push({ id, op: 'insert', text: markers[markerIdx]!.char });
    markerIdx++;
  }

  return result;
}

function applySemanticCleanup(changes: TrackedDiffSegment[], id: string): DiffSegment[] {
  // Reconstruct old and new text from changes, then re-diff with word-level semantic cleanup.
  let oldText = '';
  let newText = '';
  for (const change of changes) {
    if (change.op === 'equal') {
      oldText += change.text;
      newText += change.text;
    } else if (change.op === 'delete') {
      oldText += change.text;
    } else {
      newText += change.text;
    }
  }

  /**
   * Strip range markers from newText before diffing. This prevents
   * diff_cleanupSemantic from eliminating short punctuation equalities
   * that sit next to marker insertions (e.g., ".⟭" absorbing the period).
   */
  const { cleanText: cleanNewText, markers } = stripRangeMarkers(newText);

  /**
   * If old and new text are identical, preserve the existing structure rather than
   * collapsing to just EQUAL. This is important for layered diffs where we want to
   * show DELETE+INSERT at word level even when they produce the same visible text.
   * This happens when restoring previously-deleted characters within a word.
   */
  if (oldText === cleanNewText) {
    // Strip the _fromPlaceholder flag and return the existing structure
    return changes.map(c => {
      if (c.op === 'equal') {
        return { op: 'equal', text: c.text };
      } else if (c.op === 'insert') {
        return { id: c.id, op: 'insert', text: c.text };
      }
      return { id: c.id, op: 'delete', text: c.text };
    });
  }

  /**
   * Diff clean texts (without markers) for accurate punctuation boundaries,
   * then re-insert markers at their original content positions.
   */
  const diffs = diffWordsSemantic(oldText, cleanNewText);
  return spliceMarkersIntoDiffs(diffs, markers, id);
}
